import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Check, X, Eye } from 'lucide-react';
import API from '@/api/client';
import { formatDateTime } from '@/utils/helpers';
import { toast } from 'sonner';

const AdminContent = () => {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchPosts();
  }, [activeTab]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const status = activeTab === 'all' ? '' : activeTab;
      const response = await API.get(`/admin/content${status ? `?status=${status}` : ''}`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (contentId) => {
    try {
      await API.put(`/admin/content/${contentId}/approve`);
      toast.success('Content approved!');
      fetchPosts();
    } catch (error) {
      console.error('Error approving content:', error);
      toast.error('Failed to approve content');
    }
  };

  const handleReject = async (contentId) => {
    try {
      await API.put(`/admin/content/${contentId}/reject`);
      toast.success('Content rejected');
      fetchPosts();
    } catch (error) {
      console.error('Error rejecting content:', error);
      toast.error('Failed to reject content');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
            alt="Physiomobile Logo"
            className="h-10"
          />
          <h1 className="font-heading text-2xl font-bold text-gray-800">Flywheel HQ</h1>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="Content Hub"
          subtitle="Review and approve therapist content submissions"
          actions={
            <Button onClick={() => window.location.href = '/admin/dashboard'} variant="outline">
              Back to Dashboard
            </Button>
          }
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">Pending Review</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <LoadingSpinner size="lg" text="Loading content..." />
        ) : (
          <div className="space-y-4">
            {posts.map((post, index) => (
              <Card
                key={post.id}
                className="p-6 hover:shadow-md transition-shadow"
                data-testid={`content-post-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-5 h-5 text-primary" />
                      <h3 className="font-heading text-xl font-semibold text-gray-800">
                        {post.title}
                      </h3>
                      <StatusBadge status={post.status} />
                    </div>

                    <p className="text-muted-foreground mb-4">{post.content}</p>

                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <div>
                        <span className="font-medium">Therapist:</span> {post.therapist_id?.slice(0, 8)}
                      </div>
                      <div>
                        <span className="font-medium">Submitted:</span> {formatDateTime(post.created_at)}
                      </div>
                      {post.status === 'approved' && (
                        <div>
                          <Eye className="w-4 h-4 inline mr-1" />
                          {post.views_count || 0} views
                        </div>
                      )}
                    </div>

                    {post.status === 'pending' && (
                      <div className="flex gap-2 mt-4">
                        <Button
                          onClick={() => handleApprove(post.id)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`approve-button-${index}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(post.id)}
                          size="sm"
                          variant="destructive"
                          data-testid={`reject-button-${index}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}

            {posts.length === 0 && (
              <Card className="p-12 text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-gray-800 mb-2">
                  No content found
                </h3>
                <p className="text-muted-foreground">
                  {activeTab === 'pending'
                    ? 'No content awaiting review'
                    : `No ${activeTab} content`}
                </p>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminContent;